/**
 * @file HeaderSection.jsx
 * @author Archana
 * @date 2024-10-29
 * @description Header component of lifecycle.
 */
import {
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // Import MUI green tick icon
import { TabPanel } from "./TabPanel";

export const DetailsPanel = ({
  isLoading,
  value,
  lead,
  handleLifecycleChange,
  handleChange,
  handleLifecycleSave,
  selectedEvent,
  lifecycleEvents,
  candidateEvents,
  toggleDrawer,
  candidateuuid,
  handlePaymentPopup,
  handleClosePopup,
  isPaymentPopupOpen,
  installments,
  submitPayment,
  getCandidateLifecyclEevents,
  countryname,
  coursename,
}) => {
  return (
    <>
      {isLoading ? (
        <Skeleton variant="rounded" width={180} height={24} />
      ) : (
        <Breadcrumbs aria-label="breadcrumb">
          <Link to="/candidates">Candidate</Link>
          <Link to={`/candidates/candidate-details/${candidateuuid}`}>
            Details
          </Link>
          <Typography sx={{ color: "text.primary" }}>Lifecycle</Typography>
        </Breadcrumbs>
      )}

      <Stack display="flex" direction={{ xs: "column", md: "row" }} rowGap={2}>
        <Stack flex={1}>
          {isLoading ? (
            <Skeleton
              sx={{ mt: 3 }}
              variant="rounded"
              width={390}
              height={24}
            />
          ) : (
            <Typography
              sx={{ mt: 2 }}
              fontWeight={700}
              fontSize={24}
              color="#222222"
            >
              Candidate Lifecycle - {lead?.name || "Lead Name"}
            </Typography>
          )}
        </Stack>
        {isLoading ? (
          <Skeleton
            sx={{ alignSelf: "end", mb: 1 }}
            variant="rounded"
            width={350}
            height={42}
          />
        ) : (
          <Stack
            display="flex"
            spacing={1}
            direction="row"
            alignSelf={{ xs: "start", md: "end" }}
            pb={1}
          >
            <Button variant="outlined" onClick={toggleDrawer}>
              View Documents
            </Button>
          </Stack>
        )}
      </Stack>

      {!isLoading && <Divider />}
      <Box
        display="flex"
        flex={1}
        flexDirection={{ xs: "column", md: "row" }}
        height="100%"
        overflow={{ xs: "unset", md: "auto" }}
      >
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 1 }}
            width={"30vw"}
            height={"100vw"}
          />
        ) : (
          <Box
            width={{ xs: "100%", md: "30%" }}
            height="100%"
            display="flex"
            flexDirection="column"
            gap="10px"
            padding="20px"
            position="relative"
            overflow={{ xs: "unset", md: "scroll" }}
          >
            {candidateEvents.map((event, index) => (
              <React.Fragment key={event.id}>
                {/* Chart Box with Tick Icon and Event Name */}
                <div className="ly-chart-box">
                  <CheckCircleIcon className="ly-tick-icon" />
                  <span className="ly-chart-text">{event.lifecycle_name}</span>
                </div>

                {/* Render dotted vertical line only if it's not the last item */}
                {index < candidateEvents.length - 1 && (
                  <div className="ly-dotted-vertical-line"></div>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
        {/* Right grey background section */}
        <Box
          flex={1}
          height="100%"
          borderRadius="12px"
          px="8px"
          py={3}
          bgcolor="#F7F7F7"
          display="flex"
          flexDirection="column"
          order={{ xs: -1, md: 0 }}
          mt={1}
        >
          <Stack direction="row" height="100%" display="flex">
            <TabPanel
              isLoading={isLoading}
              value={value}
              handleChange={handleChange}
              handleLifecycleChange={handleLifecycleChange}
              handleLifecycleSave={handleLifecycleSave}
              selectedEvent={selectedEvent}
              lifecycleEvents={lifecycleEvents}
              lead={lead}
              handlePaymentPopup={handlePaymentPopup}
              handleClosePopup={handleClosePopup}
              isPaymentPopupOpen={isPaymentPopupOpen}
              installments={installments}
              submitPayment={submitPayment}
              getCandidateLifecyclEevents={getCandidateLifecyclEevents}
              countryname={countryname}
              coursename={coursename}
            />
          </Stack>
        </Box>
      </Box>
    </>
  );
};
